import * as Android from '@/api/android';
import SetOrderDataRequest from '@/api/android/interfaces/SetOrderDataRequest';
import CustomerSearch from '@/components/CustomerSearch.vue';
import { MinOrderQtyType } from '@/enums/MinOrderQtyType';
import { PaymentMethod } from '@/enums/PaymentMethod';
import UserError from '@/error';
import i18n from '@/i18n';
import CartMenu from '@/interfaces/CartMenu';
import Order from '@/interfaces/Order';
import OrderCloudCustomer from '@/interfaces/OrderCloudCustomer';
import OrderItem from '@/interfaces/OrderItem';
import OrderItemModifier from '@/interfaces/OrderItemModifier';
import OrderItemSetMenu from '@/interfaces/OrderItemSetMenu';
import OrderPayment from '@/interfaces/OrderPayment';
import IPaymentSplit from '@/interfaces/PaymentSplit';
import PaymentSummary from '@/interfaces/PaymentSummary';
import { logger } from '@/logger';
import { useCartStore } from '@/stores/cart';
import { useCommonStore } from '@/stores/common';
import { useConfigStore } from '@/stores/config';
import { useMenuStore } from '@/stores/menu';
import { useModalStore } from '@/stores/modal';
import { usePaymentStore } from '@/stores/payment';
import { getNameByLocale } from '@/utils';
import { defineStore } from 'pinia';
import { v4 as uuid } from 'uuid';
import { computed, ref } from 'vue';

export const useOrderStore = defineStore('order', () => {
  const commonStore = useCommonStore();
  const configStore = useConfigStore();
  const modalStore = useModalStore();
  const menuStore = useMenuStore();
  const cartStore = useCartStore();
  const paymentStore = usePaymentStore();

  const isPreOrder = ref(false); // 현재 주문이 선주문 후결제인지 여부
  const hasPayment = ref(false); // 현재 주문이 결제가 되었는지 여부
  const order = ref<Order>({} as Order); // 주문 데이터
  const customer = ref<OrderCloudCustomer | null>(null); // 주문 고객 데이터

  /** 주문 결제 데이터 */
  const orderPayments = computed(() => {
    return paymentStore.summary.splits.map((split) => mapSplitsToOrderPayments(split.method, split));
  });

  /**
   * 선주문후결제 여부 설정
   * @param value 선주문후결제 여부
   */
  const setIsPreOrder = (value: boolean) => {
    isPreOrder.value = value;
  };

  /**
   * 결제를 포함 여부 설정
   * @param value 결제가 포함되어 있는지 여부
   */
  const setHasPayment = (value: boolean) => {
    hasPayment.value = value;
  };

  /**
   * 조회한 주문 데이터 세팅
   * @param _order 주문 데이터
   */
  const setOrder = (_order: Order | null) => {
    logger.info('[SetOrder] 주문 데이터 세팅', _order);
    if (_order) {
      if (!_order.Items) _order.Items = [];
      _order.Items.map((item) => (item.Key = uuid()));
      order.value = _order;

      // if (_order.CloudCustomer) {
      //   setCustomer(_order.CloudCustomer);
      // }
    } else {
      order.value = {} as Order;
    }
  };

  /**
   * 주문에 고객 데이터 세팅
   * @param customer 고객 데이터
   */
  const setCustomer = (cloudCustomer: OrderCloudCustomer | null) => {
    customer.value = cloudCustomer;
  };

  /**
   * 주문 수량 조회. 첫 주문 시에만 최소주문수량 사용 옵션인 경우 이미 주문한 메뉴의 수량을 반환
   * @param itemCode 아이템 코드
   * @returns 주문 수량
   */
  const getOrderedQty = (itemCode: string): number => {
    return configStore.config.MinOrderQtyType === MinOrderQtyType.FirstOnly
      ? order.value.Items?.find((item) => item.ITEM_CODE === itemCode)?.QTY || 0
      : 0;
  };

  /** 장바구니 데이터 전체를 주문 요청 데이터에 추가 */
  const addOrderItem = (cartMenus: CartMenu[]) => {
    const newItems = cartMenus.map((cartMenu) => covertCartMenuToOrderMenu(cartMenu));
    if (order.value) {
      order.value.Items = order.value.Items.concat(newItems);
    }
  };

  /**
   * 분할 결제 데이터를 주문 결제 데이터로 변환하여 반환
   * @param method 결제 수단
   * @param split 분할 결제 데이터
   * @returns 변환된 주문 결제 데이터
   */
  const mapSplitsToOrderPayments = (method: PaymentMethod, split: IPaymentSplit) => {
    switch (method) {
      case PaymentMethod.Cash:
        return mapCashSplitsToOrderPayments(split);
      case PaymentMethod.Card:
      case PaymentMethod.Easy:
        return mapCardOrEasySplitsToOrderPayments(split);
      case PaymentMethod.Point:
        return mapPointSplitsToOrderPayments(split);
      default:
        throw new Error('지원하지 않는 결제 수단입니다.');
    }
  };

  const mapCashSplitsToOrderPayments = (split: IPaymentSplit) => {
    return {
      PaymentSection: 'PY100',
      AppType: '3',
      PayAmt: split.amount,
      FreeAmount: split.taxFree,
      Tax: split.tax,
      Deposit: split.deposit,
      PreTip: split.preTip,
      IsCashReceipt: '0',
    } satisfies OrderPayment;
  };

  const mapCardOrEasySplitsToOrderPayments = (split: IPaymentSplit) => {
    return {
      PaymentSection: split.paymentApproval?.PaymentSection || 'PY101',
      AppType: '1',
      AppNum: split.paymentApproval?.AuthNumber,
      AppDate: split.paymentApproval?.AuthDate,
      PayAmt: split.amount,
      FreeAmount: split.taxFree,
      Tax: split.tax,
      Deposit: split.deposit,
      PreTip: split.preTip,
      INMM: split.installment || 0,
      MerchantNumber: split.paymentApproval?.MerchantNumber,
      IssueCode: split.paymentApproval?.IssueCode,
      IssueCard: split.paymentApproval?.IssueCard,
      PurchaseCode: split.paymentApproval?.PurchaseCode,
      PurchaseCard: split.paymentApproval?.PurchaseCard,
      WCC: split.paymentApproval?.WCC,
      TerminalID: split.paymentApproval?.TerminalID,
      IDStr: split.paymentApproval?.CardNumber,
      IsCashReceipt: '0',
      Detail: split.paymentApproval?.Detail,
    } satisfies OrderPayment;
  };

  const mapPointSplitsToOrderPayments = (split: IPaymentSplit) => {
    return {
      PaymentSection: 'PY105',
      AppType: '3',
      AppNum: split.point?.ApproveCode,
      PayAmt: split.amount,
      FreeAmount: split.taxFree,
      Tax: split.tax,
      Deposit: split.deposit,
      PreTip: split.preTip,
      IDStr: split.point?.CardNo,
      Detail: {
        Data1: split.point?.PointBalance.toString(),
        Data2: split.point?.CustomerCode,
        Data3: split.point?.CardNo,
        Data4: split.point?.CustomerName,
        Data5: customer.value?.Mobile,
        Data6: customer.value?.Bond_TAMT?.toString(),
        Data7: customer.value?.Debt_TAMT?.toString(),
        Data10: split.point?.CheckNo,
      },
    } satisfies OrderPayment;
  };

  /**
   * 장바구니 메뉴 데이터를 주문 메뉴 데이터로 변환
   * @param cartMenu 장바구니 메뉴 데이터
   * @returns 주문 메뉴 데이터
   */
  const covertCartMenuToOrderMenu = (cartMenu: CartMenu): OrderItem => {
    // 메뉴 데이터 조회
    const menu = menuStore.getMenu(cartMenu.GroupCode, cartMenu.ItemCode);

    // 유효성 검사
    if (!menu) throw new Error('메뉴 데이터를 찾을 수 없습니다.');

    // 장바구니 토핑 데이터를 주문용 토핑 데이터로 변환
    const orderModifiers = cartMenu.Toppings.map((cartTopping) => {
      // 토핑 데이터 조회
      const topping = menuStore.getTopping(cartTopping.GroupCode.toString(), cartTopping.OptionCode);

      // 주문용 토핑 데이터 생성
      const orderItemModifier: OrderItemModifier = {
        Name: getNameByLocale(topping),
        Modifier_Grp_Code: cartTopping.GroupCode.toString(),
        Modifier_Entity_Code: cartTopping.OptionCode,
        Add_Section: topping.AddCode,
        Add_Price: menuStore.getToppingPrice(topping, menu.IsTax2),
        Add_SectionName: '',
        QTY: cartTopping.Qty,
      };

      return orderItemModifier;
    });

    // 장바구니 세트 메뉴 데이터를 주문용 세트 메뉴 데이터로 변환
    const orderSetMenus = cartMenu.SetMenus.map((cartSetMenu) => {
      // 세트 메뉴 데이터 조회
      const setMenu = menuStore.getSetMenu(
        cartSetMenu.MenuItemCode,
        Number(cartSetMenu.GroupCode),
        cartSetMenu.OptionCode,
      );

      // 주문용 세트 메뉴 데이터 생성
      const orderItemSetMenu: OrderItemSetMenu = {
        ItemType: null,
        SetItem_Level: cartSetMenu.GroupCode.toString(),
        QTY: 1,
        Price: menuStore.getSetMenuPrice(setMenu, menu.IsTax2),
        ITEM_CODE: cartSetMenu.OptionCode,
        Name0: getNameByLocale(setMenu),
      };

      return orderItemSetMenu;
    });

    // 토핑 추가 금액 조회
    const modifiersAmount = orderModifiers.reduce((acc, modifier) => acc + modifier.Add_Price * modifier.QTY, 0);
    // 세트메뉴 추가 금액 조회
    const setMenuAmount = orderSetMenus.reduce((acc, setMenu) => acc + setMenu.Price, 0);

    // 메뉴 타입으로 주문용 아이템 타입 조회
    let itemType = 'None';
    switch (menu.MenuType) {
      case 'M':
        itemType = 'ModifierMenu';
        break;
      case 'S':
        itemType = 'SubMenu';
        break;
    }

    // 총 금액 (할인 적용 전. 세금 별도인 경우 세금 포함)
    const amount = menuStore.getMenuAmount(
      menu.Price,
      modifiersAmount + setMenuAmount,
      cartMenu.Qty,
      true,
      menu.IsTax,
      menu.IsTax2,
    );

    // 주문용 아이템 데이터 생성 후 리턴
    const orderItem: OrderItem = {
      ItemType: itemType,
      QTY: cartMenu.Qty,
      Price: menuStore.getMenuPrice(menu),
      SetItem_Add_Price: setMenuAmount,
      Modifier_Add_Price: modifiersAmount,
      Amt: amount,
      ITEM_CODE: cartMenu.ItemCode,
      Name0: getNameByLocale(menu),
      Modifier: orderModifiers,
      SetMenu: orderSetMenus,
      IsEdit: 'true',
      DiscountAmt: menu.DiscountAmount * cartMenu.Qty || 0,
      DiscountType: menu.DiscountType,
      EventName: menu.EventName,
      EventCode: menu.EventCode || 0,
      EventType: menu.EventType,
      DiscountSection: menu.DiscountSection,
      DiscountRate: menu.DiscountType === '1' ? menu.DiscountPercentage || 0 / 100 : 0,
    };
    return orderItem;
  };

  /**
   * POS에서 주문 조회 및 세팅
   * @param isRetry 자동 재시도 여부 (결제 이후)
   * @returns
   */
  const fetchOrder = async (isRetry: boolean) => {
    const { t } = i18n.global;

    // POS에서 주문 조회
    try {
      const response = await Android.getOrderDataForOrderAsync();
      if (response.RET_CODE !== 0) throw new UserError(response.RET_MSG);
      // 조회한 주문 세팅
      setOrder(response.DATA);
    } catch (error: any) {
      let errorMessage = t('error.retry');
      if (error instanceof UserError) {
        errorMessage = `[${error.message}]\n${errorMessage}`;
      }
      // 무한 재시도 여부 검사 (결제 이후)
      if (isRetry) {
        await commonStore.showDialog({
          type: 'alert',
          message: errorMessage,
          autoCloseSeconds: 5,
        });

        // 주문 재조회 (재귀함수)
        await fetchOrder(isRetry);
        return;
      } else {
        if (error instanceof UserError) {
          throw error;
        } else {
          throw new UserError(t('error.common'));
        }
      }
    }
  };

  /**
   * 주문 전송
   * @param isRetry 자동 재시도 여부 (결제 이후)
   * @param shouldEarnPoint 고객 포인트 적립 여부. 선주문후결제인 경우 주문할때는 적립 처리 하지 않기 위한 플래그
   * @returns
   */
  const sendOrder = async (isRetry: boolean, shouldEarnPoint: boolean | null = true) => {
    const { t } = i18n.global;

    while (true) {
      // 주문 전송
      try {
        // 주문에 고객 데이터가 없는데 회원 기능을 사용하는 경우 회원 검색 후 업데이트하여 주문 재전송
        if (configStore.config.IsCustomerUsed && shouldEarnPoint && !order.value.CloudCustomer && !customer.value) {
          // 회원 조회 후 회원 정보 받기
          const _customer = await modalStore.openSyncModalAsync<typeof CustomerSearch, OrderCloudCustomer | null>(
            CustomerSearch,
            {
              type: 'EARN',
            },
          );

          // 고객 정보 세팅
          if (_customer) setCustomer(_customer);
        }

        // 주문 전송 요청 데이터 생성
        const setOrderDataRequest: SetOrderDataRequest = {
          data: {
            DATA: {
              HDate: order.value.HDate || null,
              HNO: order.value.HNO || null,
              Pos_Code: order.value.Pos_Code || configStore.config.PosCode || '',
              Tab_Code: order.value.Tab_Code || configStore.config.TableCode || '',
              TableName: order.value.TableName || null,
              CustomerCnt: cartStore.cart.Headcount || 1,
              CloudCustomer: order.value.CloudCustomer || customer.value || null,
              Items: order.value.Items || [],
              Payments: orderPayments.value,
            },
          },
        };

        const response = await Android.setOrderDataAsync(setOrderDataRequest);
        if (response.RET_CODE !== 0) throw new UserError(response.RET_MSG);

        break; // 주문 전송 성공 시 루프 탈출
      } catch (error: any) {
        let errorMessage = t('error.retry');
        if (error instanceof UserError) {
          errorMessage = `[${error.message}]\n${errorMessage}`;
        }

        // 무한 재시도 여부 검사 (결제 이후)
        if (isRetry) {
          await commonStore.showDialog({
            type: 'alert',
            message: errorMessage,
            autoCloseSeconds: 5,
          });
        } else {
          // 무한 재시도 하지 않는 경우 주문 전송 실패 시 결제 분할 데이터 삭제
          paymentStore.removeLastPaymentSplit();
          if (error instanceof UserError) {
            throw error;
          } else {
            throw new UserError(t('error.common'));
          }
        }
      }
    }

    // 주문 결제 초기화
    paymentStore.setSummary({
      amount: 0,
      taxFree: 0,
      tax: 0,
      deposit: 0,
      preTip: 0,
      splits: [],
    } satisfies PaymentSummary);

    // 장바구니 초기화
    cartStore.initializeCart();

    // 회원 초기화
    setCustomer(null);
  };

  return {
    isPreOrder,
    hasPayment,
    order,
    customer,
    orderPayments,

    setIsPreOrder,
    setHasPayment,
    setOrder,
    setCustomer,
    getOrderedQty,
    addOrderItem,
    fetchOrder,
    sendOrder,
  };
});
